import * as React from 'react';
import { createRoute } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { rootRoute } from './__root';
import { AppShell } from '@/components/AppShell';
import { api } from '@/lib/api';
import { errorMessage as toMessage } from '@/lib/errors';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';

export const settingsNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/notifications',
  component: NotificationSettingsPage,
});

const OPTIONAL_KINDS: Array<{ id: string; label: string; description: string }> = [
  { id: 'idea_status_change', label: 'Idea decisions', description: 'Approved / rejected / parked on ideas you sponsor' },
  { id: 'idea_resurfaced', label: 'Parked idea resurfaced', description: 'Auto-resurface on the revisit date' },
  { id: 'initiative_review_due', label: 'Initiative review due', description: 'A project you sponsor needs a decision' },
  { id: 'payroll_run_status', label: 'Payroll run status', description: 'Preview / lock / post transitions' },
  { id: 'period_status_change', label: 'Period close status', description: 'Soft close, hard close, or reopen' },
  { id: 'leave_status_change', label: 'Leave decisions', description: 'Approvals or rejections on your leave requests' },
];

const FORCED_KINDS = new Set(['approval', 'mention']);

function NotificationSettingsPage(): JSX.Element {
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: ['notification-preferences'],
    queryFn: () => api.getNotificationPreferences(),
  });
  const [digestHour, setDigestHour] = React.useState('9');
  const [immediate, setImmediate] = React.useState<Set<string>>(new Set());
  const [error, setError] = React.useState<string | null>(null);
  const [saved, setSaved] = React.useState(false);

  React.useEffect(() => {
    if (prefsQuery.data) {
      setDigestHour(String(prefsQuery.data.digestHourIst));
      setImmediate(new Set(prefsQuery.data.immediateKinds));
    }
  }, [prefsQuery.data]);

  const save = useMutation({
    mutationFn: () =>
      api.updateNotificationPreferences({
        digestHourIst: Math.max(0, Math.min(23, parseInt(digestHour, 10) || 9)),
        immediateKinds: [...immediate],
      }),
    onSuccess: async () => {
      setSaved(true);
      setError(null);
      await queryClient.invalidateQueries({ queryKey: ['notification-preferences'] });
      setTimeout(() => setSaved(false), 2000);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const toggle = (id: string): void => {
    setImmediate((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  return (
    <AppShell>
      <div className="flex flex-col gap-2">
        <h1 className="font-display text-4xl font-semibold tracking-tight md:text-5xl">
          Notification preferences
        </h1>
        <p className="text-base text-[hsl(var(--muted-foreground))]">
          Approvals and mentions are always immediate — everything else can join the daily digest.
        </p>
      </div>

      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Save failed</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {saved ? (
        <Alert>
          <AlertTitle>Saved</AlertTitle>
          <AlertDescription>Preferences updated.</AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Daily digest</CardTitle>
          <CardDescription>Digest fires at this hour in IST.</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="h">Hour (0-23)</Label>
              <Input
                id="h"
                type="number"
                min={0}
                max={23}
                value={digestHour}
                onChange={(e) => setDigestHour(e.currentTarget.value)}
                className="w-28"
              />
            </div>
            <div className="text-xs text-[hsl(var(--muted-foreground))]">
              Digest arrives at approximately {digestHour.padStart(2, '0')}:00 IST daily.
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Immediate delivery</CardTitle>
          <CardDescription>
            Check to receive an email as soon as the event happens (instead of waiting for the digest).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <ul className="flex flex-col gap-2 text-sm">
            <li className="flex items-start gap-3 rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--muted))] p-3">
              <input type="checkbox" checked disabled className="mt-1" />
              <div>
                <div className="font-medium">Approvals & mentions</div>
                <div className="text-xs text-[hsl(var(--muted-foreground))]">
                  Always immediate — cannot be muted.
                </div>
              </div>
            </li>
            {OPTIONAL_KINDS.map((k) => (
              <li key={k.id} className="flex items-start gap-3 rounded-lg border border-[hsl(var(--border))] p-3">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={immediate.has(k.id)}
                  onChange={() => toggle(k.id)}
                />
                <div>
                  <div className="font-medium">{k.label}</div>
                  <div className="text-xs text-[hsl(var(--muted-foreground))]">{k.description}</div>
                </div>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => save.mutate()} disabled={save.isPending}>
          {save.isPending ? 'Saving…' : 'Save preferences'}
        </Button>
      </div>

      {[...immediate].some((k) => !FORCED_KINDS.has(k) && !OPTIONAL_KINDS.find((x) => x.id === k)) ? (
        <p className="text-xs text-[hsl(var(--muted-foreground))]">
          Other kinds also enabled: {[...immediate].filter((k) => !FORCED_KINDS.has(k) && !OPTIONAL_KINDS.find((x) => x.id === k)).join(', ')}
        </p>
      ) : null}
    </AppShell>
  );
}
